import {
  Alert,
  Anchor,
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useLocation } from 'wouter';
import { formatDate } from '@/lib/helpers';
import {
  type MondayLeadImportCandidate,
  useImportMondayLead,
  useMondayLeadImportPreview,
} from '@/models/leads';
import { StatusBadge } from './StatusBadge';

function Fact({
  label,
  value,
  lineClamp,
}: {
  label: string;
  value: string | null;
  lineClamp?: number;
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text
        size="sm"
        lineClamp={lineClamp}
        title={lineClamp && value ? value : undefined}
        style={{ overflowWrap: 'anywhere' }}
      >
        {value || '—'}
      </Text>
    </div>
  );
}

// Values past this length get their own full-width row instead of a grid cell.
const LONG_VALUE = 48;

export function ImportMondayLeadModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const [, nav] = useLocation();
  const preview = useMondayLeadImportPreview();
  const importLead = useImportMondayLead();
  const [itemId, setItemId] = useState<number | string>('');
  const [candidate, setCandidate] = useState<MondayLeadImportCandidate | null>(
    null,
  );

  const handleClose = () => {
    setCandidate(null);
    setItemId('');
    onClose();
  };

  const showError = (err: unknown) =>
    notifications.show({ color: 'red', message: String(err) });

  const loadPreview = () => {
    if (!itemId) return;
    preview.mutate(Number(itemId), {
      onSuccess: setCandidate,
      onError: showError,
    });
  };

  const confirmImport = () => {
    if (!candidate) return;
    importLead.mutate(candidate.mondayItemId, {
      onSuccess: ({ lead }) => {
        handleClose();
        nav(`~/leads/${lead.id}`);
      },
      onError: showError,
    });
  };

  const canImport =
    candidate && candidate.missing.length === 0 && !candidate.existingLeadId;

  const unmapped = Object.entries(candidate?.unmapped ?? {}).filter(
    ([field]) => field !== 'extraReferringAgents',
  ) as [string, string][];

  const extraReferringAgents = candidate?.unmapped.extraReferringAgents ?? [];

  // Lineage problems read differently from an unmapped dropdown label: nothing is
  // coerced to a fallback, the link is simply not carried over.
  const lineageWarnings = candidate?.lineageWarnings ?? [];

  // Everything marketing-related that carries a value — imported fields are
  // never hidden from the preview.
  const marketing: [string, string][] = candidate
    ? (
        [
          [
            'Marketing ID',
            candidate.lineage.mondayMarketingItemId == null
              ? null
              : String(candidate.lineage.mondayMarketingItemId),
          ],
          [
            'Monday contact',
            candidate.lineage.mondayContactItemId == null
              ? null
              : String(candidate.lineage.mondayContactItemId),
          ],
          ['Origin URL', candidate.lead.originUrl],
          ['Path', candidate.lead.path],
          ['UTM source', candidate.lead.utmSource],
          ['UTM medium', candidate.lead.utmMedium],
          ['UTM campaign', candidate.lead.utmCampaign],
          ['Marketing campaign', candidate.lead.marketingCampaign],
          ['Marketing channel', candidate.lead.marketingChannel],
          ['Affiliate', candidate.lead.affiliate],
          ['Affiliate source', candidate.lead.affiliateSource],
          ['IP', candidate.lead.ip],
          [
            'GDPR consent',
            candidate.lead.gdprConsent == null
              ? null
              : candidate.lead.gdprConsent
                ? 'Given'
                : 'Declined',
          ],
        ] as [string, string | null][]
      ).filter((entry): entry is [string, string] => Boolean(entry[1]))
    : [];

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import lead from Monday"
      size="lg"
    >
      <Stack gap="sm">
        <NumberInput
          label="Monday Lead Item ID"
          description="The item must live on the Leads board"
          value={itemId}
          onChange={(v) => {
            setItemId(v);
            setCandidate(null);
          }}
        />

        {candidate && (
          <Paper withBorder p="sm">
            <Stack gap="sm">
              <Group gap="xs">
                <Text fw={600} fz="sm">
                  {[candidate.lead.firstName, candidate.lead.lastName]
                    .filter(Boolean)
                    .join(' ') || candidate.itemName}
                </Text>
                <StatusBadge status={candidate.lead.status} />
                <Badge size="sm" variant="light">
                  {candidate.lead.source}
                </Badge>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                <Fact label="Email" value={candidate.lead.email} />
                <Fact label="Phone" value={candidate.lead.phone} />
                <Fact label="Whatsapp" value={candidate.lead.whatsapp} />
                <Fact label="Telegram" value={candidate.lead.telegram} />
                <Fact label="Website" value={candidate.lead.website} />
                <Fact
                  label="Business model"
                  value={candidate.lead.businessModel}
                />
                <Fact
                  label="Last contact"
                  value={
                    candidate.lead.lastContactAt
                      ? formatDate(candidate.lead.lastContactAt)
                      : null
                  }
                />
                <Fact
                  label="Assignee"
                  value={
                    candidate.assigneeUserId
                      ? (candidate.assigneeMondayEmail ??
                        'you (Sales Representative)')
                      : 'unassigned'
                  }
                />
                <Fact
                  label="Source selected"
                  value={candidate.lead.sourceLabels.join(', ') || null}
                />
                <Fact
                  label="Source detail"
                  value={candidate.lead.sourceDetail}
                />
              </SimpleGrid>
              {candidate.lead.request && (
                <Fact
                  label="Request"
                  value={candidate.lead.request}
                  lineClamp={3}
                />
              )}

              {candidate.referringAgent && (
                <div>
                  <Text size="xs" c="dimmed">
                    Referring agent
                  </Text>
                  <Group gap={6}>
                    <Text size="sm">{candidate.referringAgent.name}</Text>
                    <Badge
                      size="sm"
                      variant="light"
                      color={
                        candidate.referringAgent.existing ? 'blue' : 'green'
                      }
                    >
                      {candidate.referringAgent.existing ? 'existing' : 'new'}
                    </Badge>
                  </Group>
                </div>
              )}

              {marketing.length > 0 && (
                <>
                  <Divider
                    label="Marketing"
                    labelPosition="left"
                    variant="dashed"
                  />
                  {marketing.some(([, v]) => v.length <= LONG_VALUE) && (
                    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                      {marketing
                        .filter(([, v]) => v.length <= LONG_VALUE)
                        .map(([label, value]) => (
                          <Fact key={label} label={label} value={value} />
                        ))}
                    </SimpleGrid>
                  )}
                  {marketing
                    .filter(([, v]) => v.length > LONG_VALUE)
                    .map(([label, value]) => (
                      <Fact
                        key={label}
                        label={label}
                        value={value}
                        lineClamp={2}
                      />
                    ))}
                </>
              )}

              {candidate.assigneeMondayEmail && !candidate.assigneeUserId && (
                <Alert color="yellow">
                  <Text fz="sm">
                    Monday owner {candidate.assigneeMondayEmail} has no
                    backoffice user — the lead will stay unassigned unless you
                    are a Sales Representative.
                  </Text>
                </Alert>
              )}

              {unmapped.map(([field, raw]) => (
                <Alert color="yellow" key={field}>
                  <Text fz="sm">
                    Monday {field} "{raw}" has no backoffice equivalent — it is
                    {field === 'source'
                      ? ` imported as ${candidate.lead.source}, and the original wording is kept`
                      : field === 'status'
                        ? ' imported as New Lead'
                        : ' dropped'}
                    .
                  </Text>
                </Alert>
              ))}

              {lineageWarnings.map((warning) => (
                <Alert color="yellow" key={warning}>
                  <Text fz="sm">
                    {warning}. The lead is imported without that link, so
                    nothing downstream will point back to the Marketing record.
                  </Text>
                </Alert>
              ))}

              {extraReferringAgents.length > 0 && (
                <Alert color="yellow">
                  <Text fz="sm">
                    Monday item also links {extraReferringAgents.join(', ')} as
                    referring agent{extraReferringAgents.length > 1 && 's'} —
                    only the first will be attached.
                  </Text>
                </Alert>
              )}

              {candidate.duplicates.map((d) => (
                <Alert color="orange" key={`${d.kind}-${d.on}-${d.id}`}>
                  <Text fz="sm">
                    An existing {d.kind} shares this {d.on} —{' '}
                    <Anchor
                      fz="sm"
                      onClick={() => {
                        handleClose();
                        nav(`~/${d.kind}s/${d.id}`);
                      }}
                    >
                      open {d.kind} #{d.id}
                    </Anchor>
                    . Importing anyway will create a second record.
                  </Text>
                </Alert>
              ))}

              {candidate.existingLeadId && (
                <Alert color="blue">
                  <Text fz="sm">
                    This Monday item was already imported —{' '}
                    <Anchor
                      fz="sm"
                      onClick={() => {
                        handleClose();
                        nav(`~/leads/${candidate.existingLeadId}`);
                      }}
                    >
                      open the existing lead
                    </Anchor>
                    .
                  </Text>
                </Alert>
              )}

              {candidate.missing.length > 0 && (
                <Alert color="orange">
                  <Text fz="sm">
                    Cannot import — missing required details:{' '}
                    {candidate.missing.join(', ')}. Complete them on the Monday
                    item first.
                  </Text>
                </Alert>
              )}
            </Stack>
          </Paper>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          {!candidate ? (
            <Button
              loading={preview.isPending}
              disabled={!itemId}
              onClick={loadPreview}
            >
              Preview
            </Button>
          ) : (
            <Button
              loading={importLead.isPending}
              disabled={!canImport}
              onClick={confirmImport}
            >
              Confirm Import
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
